import type { CapabilityRegistry } from '@trimble/web3d';
import {
  DELETE_ALL_MENU_ITEM_ICON,
  type ControlMenuItemType,
} from '@trimble/ui-components/solidjs/control-menu/types';
import { t } from '@trimble/i18n';
import { ClippingCapability } from '@trimble/web3d-capabilities';
import {
  Accessor,
  batch,
  createEffect,
  createMemo,
  createSignal,
} from 'solid-js';

export interface UseToolbarClipStateResult {
  readonly clipMenuItems: Accessor<ControlMenuItemType[]>;
  readonly isAnyClippingActive: Accessor<boolean>;
  deactivateAllClipping(): void;
}

/**
 * @intent Owns clip-pane toolbar dropdown state while keeping clip planes
 * pane-scoped in Web3D capabilities.
 */
export const useToolbarClipState = (
  capabilityRegistry: CapabilityRegistry
): UseToolbarClipStateResult => {
  const [selectedClipItemId, setSelectedClipItemId] = createSignal<
    string | undefined
  >();

  const clippingCapabilities = createMemo((): ClippingCapability[] =>
    capabilityRegistry.getPaneCapabilities(ClippingCapability)
  );

  const isAnyClippingActive = createMemo((): boolean =>
    clippingCapabilities().some((capability) => capability.isActive())
  );

  const activeClipItemId = createMemo((): string | undefined =>
    isAnyClippingActive() ? selectedClipItemId() : undefined
  );

  const deactivateAllClipping = (): void =>
    batch(() => {
      clippingCapabilities().forEach((capability) => capability.deactivate());
      setSelectedClipItemId(undefined);
    });

  // Clip creation is armed across every pane at once but is single-shot per
  // pane: placing a plane/box only deactivates the pane that received it,
  // leaving sibling panes armed and the toolbar looking active. Once any one
  // of them completes, stop the rest too so the tool state is unambiguous:
  // either every pane is armed, or none is, matching what the button shows.
  const activeClippingCount = createMemo(
    (): number => clippingCapabilities().filter((c) => c.isActive()).length
  );

  let previousActiveClippingCount = 0;
  createEffect(() => {
    const count = activeClippingCount();
    if (count > 0 && count < previousActiveClippingCount) {
      deactivateAllClipping();
    }
    previousActiveClippingCount = count;
  });

  const startClipTool = (
    itemId: string,
    start: (c: ClippingCapability) => void
  ): void =>
    batch(() => {
      if (activeClipItemId() === itemId) {
        deactivateAllClipping();
        return;
      }
      setSelectedClipItemId(itemId);
      clippingCapabilities().forEach(start);
    });

  const clipMenuItems = createMemo((): ControlMenuItemType[] => [
    {
      id: 'clip-plane',
      type: 'action' as const,
      label: t('general.clipping.clipPlane'),
      icon: 'svg-clip-plane',
      selected: activeClipItemId() === 'clip-plane',
      onClick: () =>
        startClipTool('clip-plane', (capability) =>
          capability.startClipPlaneCreation()
        ),
    },
    {
      id: 'vert-clip',
      type: 'action' as const,
      label: t('general.clipping.verticalClipPlane'),
      icon: 'svg-vertical-clip-plane',
      selected: activeClipItemId() === 'vert-clip',
      onClick: () =>
        startClipTool('vert-clip', (capability) =>
          capability.startVerticalClipPlaneCreation()
        ),
    },
    {
      id: 'clip-box',
      type: 'action' as const,
      label: t('general.clipping.clipBox'),
      icon: 'svg-clip-box',
      selected: activeClipItemId() === 'clip-box',
      onClick: () =>
        startClipTool('clip-box', (capability) =>
          capability.startClipBoxCreation()
        ),
    },
    {
      id: 'clip-divider',
      type: 'divider' as const,
    },
    {
      id: 'delete-clips',
      type: 'action' as const,
      label: t('general.clipping.deleteAllClipPlanes'),
      icon: DELETE_ALL_MENU_ITEM_ICON,
      replayable: false,
      onClick: () =>
        clippingCapabilities().forEach((capability) => capability.clearAll()),
    },
  ]);

  return {
    clipMenuItems,
    isAnyClippingActive,
    deactivateAllClipping,
  };
};
